import {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  SideBarDef
} from "ag-grid-enterprise";
import { numberFilterParams } from "components/cell-editor/numberCellEditor";
import { WarrantyValueFormatter } from "components/cell-editor/warranty-cell-editor";
import {
  CellValueRendererWithEditIcon,
  dueDateCellRenderer,
  LBSCellRenderer,
  materialDueDateHeaderComponent,
  riskCellRenderer,
  SubmittalLinksEntityCellRenderer,
  TaskLinksCellRenderer
} from "components/cell-renders";
import {
  ArrayStringComparator,
  ArrayStringGetQuickFilterText,
  materialEarlyReleaseCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  coldefHiddenField,
  coldefSingleField,
  coldefUserV2
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  headerValueGetter,
  shouldSpanRow,
  dateCellValueGetter,
  headerTooltipValueGetter
} from "pages/reports-menu/feature-custom-reports/utils/utils";
import { useMemo } from "react";
import CustomTreeFilter from "components/grid-filters/custom-tree-filter";
import { DateFilter as DateFilterUtils } from "utils/dateutils";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { ContextDatasetConfigType } from "../models";
import {
  LINKED_SUBMITTALS_KEY,
  LINKED_ACTIVITIES_KEY
} from "../custom-reports-constants";

function useReportColumnDef({
  groupByField,
  linkedEntityDisplayType,
  isLBSFeatureEnabled,
  projectId
}: {
  groupByField: ContextDatasetConfigType["groupByField"];
  linkedEntityDisplayType: ContextDatasetConfigType["linkedEntity"];
  isLBSFeatureEnabled: Boolean;
  projectId: string;
}) {
  const submittalDisplayType = useMemo(() => {
    if (!linkedEntityDisplayType) return undefined;
    return linkedEntityDisplayType.find(
      (entity: any) => entity.feature_type_id === 1
    )?.display_type;
  }, [linkedEntityDisplayType]);

  const activityDisplayType = useMemo(() => {
    if (!linkedEntityDisplayType) return undefined;
    return linkedEntityDisplayType.find(
      (entity: any) => entity.feature_type_id === 9
    )?.display_type;
  }, [linkedEntityDisplayType]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      cellRenderer: CellValueRendererWithEditIcon,
      headerTooltipValueGetter
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 300,
      headerValueGetter: (params: any) => {
        if (groupByField === "none") return "No Grouping";
        const groupColHeader = headerValueGetter(params, groupByField);
        return `Group by ${groupColHeader}`;
      },
      colId: groupByField || "none",
      filter: "agGroupColumnFilter",
      menuTabs: ["filterMenuTab"],
      sortable: true,
      suppressMovable: true,
      lockPosition: true,
      cellRendererParams: {
        suppressCount: true
      },
      spanRows: true,
      autoHeight: true,
      cellClass: `dataset_group_column dataset_groupby_class_${groupByField}`
    };
  }, [groupByField]);

  const sideBarConfig: SideBarDef = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressSideButtons: true,
            suppressColumnSelectAll: true
          }
        }
      ],
      position: "right"
    };
  }, []);

  const columnDefs: (ColDef<any> | ColGroupDef<any>)[] = useMemo(() => {
    return [
      {
        field: "sequence_id",
        colId: "sequence_id",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "sequence_id");
        },
        width: 120,
        spanRows: true
      },
      coldefSingleField({
        colId: "title",
        field: "title",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "title");
        },
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "materials",
          projectId
        },
        getQuickFilterText: twoRowGetQuickFilterText,
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "title",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        filter: false
      }),
      coldefHiddenField({
        field: "description",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "description");
        }
      }),
      coldefSingleField({
        colId: "milestone_state",
        field: "milestone_state",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "milestone_state");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "milestone_state",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "wf_override_reason",
        colId: "wf_override_reason",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "wf_override_reason");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "wf_override_reason",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: materialEarlyReleaseCellRenderer,
        valueGetter: ({ data }: any) => {
          if (data?.wf_override_reason) return "Yes";
          return "";
        }
      }),
      {
        field: "planned_next_milestone_date",
        colId: "planned_next_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_next_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_next_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        field: "next_planned_deadline",
        colId: "next_planned_deadline",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "next_planned_deadline");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "next_planned_deadline",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      coldefSingleField({
        field: "next_action",
        colId: "next_action",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "next_action");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "next_action",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      {
        field: "final_deadline_date",
        colId: "final_deadline_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "final_deadline_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "final_deadline_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        field: "projected_last_milestone_date",
        colId: "projected_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },

      {
        field: "due_date",
        colId: "due_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "due_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "due_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: (params: ICellRendererParams) => {
          return dueDateCellRenderer(params, false);
        },
        headerComponentParams: materialDueDateHeaderComponent,
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      coldefSingleField({
        field: "lead_time",
        colId: "lead_time",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "lead_time");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "lead_time",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        colId: "risk",
        field: "risk",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "risk");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "risk",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: (params: ICellRendererParams) => {
          return riskCellRenderer(params, false);
        }
      }),
      coldefSingleField({
        field: "planned_float",
        colId: "planned_float",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_float");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_float",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "actual_float",
        colId: "actual_float",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_float");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_float",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),

      coldefSingleField({
        colId: "spec_sections",
        field: "spec_sections",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "spec_sections");
        },
        rowGroup: groupByField === "spec_sections",
        hide: groupByField === "spec_sections",
        suppressColumnsToolPanel: groupByField === "spec_sections",
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "spec_sections",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "purchase_order",
        colId: "purchase_order",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "purchase_order");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "purchase_order",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "tag_name",
        colId: "tag_name",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "tag_name");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "tag_name",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "date_block_template_name",
        colId: "date_block_template_name",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "date_block_template_name");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "date_block_template_name",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),

      ...(isLBSFeatureEnabled
        ? [
            coldefSingleField({
              field: "project_locations",
              colId: "project_locations",
              headerValueGetter: (params: any) => {
                return headerValueGetter(params, "project_locations");
              },
              spanRows: (params: any) => {
                return shouldSpanRow({
                  path: "project_locations",
                  nodeAData: params.nodeA.data,
                  nodeBData: params.nodeB.data
                });
              },
              valueGetter: ({ data }) => {
                if (
                  !data ||
                  !data.project_locations ||
                  !data.project_locations.length
                )
                  return [];
                return data.project_locations.map((loc: any) => ({
                  id: loc.id || loc.path,
                  name: loc.path,
                  path: loc.path
                }));
              },
              filter: CustomTreeFilter,
              cellRenderer: LBSCellRenderer,
              getQuickFilterText: ArrayStringGetQuickFilterText,
              comparator: ArrayStringComparator
            })
          ]
        : []),

      coldefSingleField({
        colId: "quantity",
        field: "quantity",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "quantity");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "quantity",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: (param: any) =>
          `${param?.data?.quantity ?? ""} ${
            param?.data?.material_quantity_unit ?? ""
          }`.trim()
      }),

      coldefUserV2({
        def: {
          colId: "responsible_contractor_name",
          field: "responsible_contractor_name",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "responsible_contractor_name");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "responsible_contractor_name",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "responsible_contractor_name",
          hide: groupByField === "responsible_contractor_name",
          suppressColumnsToolPanel:
            groupByField === "responsible_contractor_name",
          cellClass: "pt-3"
        },
        user: {
          id: "responsible_contractor_id",
          firstName: "responsible_contractor_name",
          lastName: "",
          dropdownType: "RC_dropdown"
        }
      }),
      coldefUserV2({
        def: {
          colId: "assignee_user",
          field: "assignee_user",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "assignee_user");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "assignee_user.id",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "assignee_user",
          hide: groupByField === "assignee_user",
          suppressColumnsToolPanel: groupByField === "assignee_user",
          cellClass: "pt-3"
        },
        user: {
          id: "assignee_user.id",
          firstName: "assignee_user.name",
          lastName: "",
          statusId: "assignee_user.status_id",
          dropdownType: "RC_ASSIGNEE_dropdown",
          dependentId: "responsible_contractor_id",
          dependentNotFoundContent: "First select a Responsible Contractor",
          companyObjField: "assignee_user.company"
        }
      }),
      coldefUserV2({
        def: {
          colId: "gc_representative",
          field: "gc_representative",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "gc_representative");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "gc_representative.id",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "gc_representative",
          hide: groupByField === "gc_representative",
          suppressColumnsToolPanel: groupByField === "gc_representative",
          cellClass: "pt-3"
        },
        user: {
          id: "gc_representative.id",
          firstName: "gc_representative.name",
          lastName: "",
          statusId: "gc_representative.status_id",
          companyObjField: "gc_representative.company"
        }
      }),
      coldefSingleField({
        colId: "shipping_location",
        field: "shipping_location",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "shipping_location");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "shipping_location",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "manufacturer",
        colId: "manufacturer",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "manufacturer");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "manufacturer",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "material_size",
        colId: "material_size",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "material_size");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "material_size",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "part_number",
        colId: "part_number",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "part_number");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "part_number",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "serial_number",
        colId: "serial_number",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "serial_number");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "serial_number",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "warranty",
        colId: "warranty",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "warranty");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "warranty",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueFormatter: WarrantyValueFormatter,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams
      }),

      {
        colId: "planned_milestone_1",
        field: "planned_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "actual_milestone_1",
        field: "actual_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "planned_last_milestone_date",
        field: "planned_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "actual_last_milestone_date",
        field: "actual_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "projected_next_milestone_date",
        field: "projected_next_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_next_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_next_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "projected_milestone_1",
        field: "projected_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        comparator: DateFilterUtils.comparator
      },

      coldefSingleField({
        colId: LINKED_SUBMITTALS_KEY,
        field: LINKED_SUBMITTALS_KEY,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, LINKED_SUBMITTALS_KEY);
        },
        minWidth: 400,
        hide: submittalDisplayType === "none",
        suppressColumnsToolPanel: submittalDisplayType === "none",
        resizable: submittalDisplayType !== "condensed",
        cellRenderer:
          submittalDisplayType === "condensed"
            ? SubmittalLinksEntityCellRenderer
            : undefined,
        marryChildren: submittalDisplayType === "inline",
        children:
          submittalDisplayType === "inline"
            ? [
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                  field: `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: true,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__title`,
                  field: `${LINKED_SUBMITTALS_KEY}__title`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__title`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: false,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__status`,
                  field: `${LINKED_SUBMITTALS_KEY}__status`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__status`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: true,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_float`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_float`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_float`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agNumberColumnFilter",
                  filterParams: numberFilterParams,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_float`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_float`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_float`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agNumberColumnFilter",
                  filterParams: numberFilterParams,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__risk`,
                  field: `${LINKED_SUBMITTALS_KEY}__risk`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__risk`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: true,
                  cellRenderer: (params: ICellRendererParams) => {
                    return riskCellRenderer(params, false);
                  },
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                }
              ]
            : undefined,

        filterParams: {
          valueFormatter: (params: any) => {
            try {
              return params?.value ? "Submittal Linked" : "No Submittal Linked";
            } catch {
              return "No Submittal Linked";
            }
          },
          keyCreator: (params: any) => {
            try {
              return params?.value ? "Submittal Linked" : "No Submittal Linked";
            } catch {
              return "No Submittal Linked";
            }
          }
        }
      }),
      coldefSingleField({
        colId: LINKED_ACTIVITIES_KEY,
        field: LINKED_ACTIVITIES_KEY,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, LINKED_ACTIVITIES_KEY);
        },

        valueGetter: ({ data }: any) => {
          return data?.tasks?.map((activity: any) => {
            const name = activity.activity_id
              ? `${activity.activity_id} - ${activity.task_name}`
              : activity.task_name;
            return { ...activity, name };
          });
        },
        hide: activityDisplayType === "none",
        suppressColumnsToolPanel: activityDisplayType === "none",
        resizable: false,
        cellRenderer: TaskLinksCellRenderer,
        filterParams: {
          valueFormatter: ({ value }: any) => {
            return value ? "Activity Linked" : "No Activities Linked";
          },
          keyCreator: ({ value }: any) => {
            return value ? "Activity Linked" : "No Activities Linked";
          }
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: LINKED_ACTIVITIES_KEY,
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      })
    ];
  }, [
    projectId,
    groupByField,
    isLBSFeatureEnabled,
    submittalDisplayType,
    activityDisplayType
  ]);

  return { columnDefs, defaultColDef, autoGroupColumnDef, sideBarConfig };
}

export default useReportColumnDef;
